import { apiSlice } from "./apiSlice";
import type {
  OdReviewRunDetail,
  OdReviewRunSummary,
  OdReviewIssue,
  OdReviewCourseOption,
  OdReviewPhase,
} from "../../services/odReview/odReview.types";

export const odReviewApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getOdReviewCourses: builder.query<
      OdReviewCourseOption[],
      { grade_id?: number; subject_id?: number }
    >({
      query: ({ grade_id, subject_id }) => ({
        url: "/admin/od-review/courses",
        method: "GET",
        params: {
          ...(grade_id !== undefined && { grade_id }),
          ...(subject_id !== undefined && { subject_id }),
        },
      }),
    }),
    getOdReviewRuns: builder.query<OdReviewRunSummary[], void>({
      query: () => ({ url: "/admin/od-review/runs", method: "GET" }),
      providesTags: [{ type: "OdReviewRun" as const, id: "LIST" }],
    }),
    getOdReviewRun: builder.query<OdReviewRunDetail, number>({
      query: (id) => ({ url: `/admin/od-review/runs/${id}`, method: "GET" }),
      providesTags: (result, error, id) => [
        { type: "OdReviewRun" as const, id },
      ],
    }),
    createOdReviewRun: builder.mutation<
      OdReviewRunSummary,
      { course_ids: number[]; phases?: OdReviewPhase[] }
    >({
      query: ({ course_ids, phases }) => ({
        url: "/admin/od-review/runs",
        method: "POST",
        data: { course_ids, ...(phases !== undefined && { phases }) },
      }),
      invalidatesTags: (result, error) =>
        error ? [] : [{ type: "OdReviewRun" as const, id: "LIST" }],
    }),
    getOdReviewIssues: builder.query<OdReviewIssue[], number>({
      query: (runId) => ({
        url: `/admin/od-review/runs/${runId}/issues`,
        method: "GET",
      }),
      providesTags: (result, error, runId) => [
        { type: "OdReviewIssue" as const, id: `RUN-${runId}` },
      ],
    }),
    acceptOdReviewIssue: builder.mutation<
      OdReviewIssue,
      { id: number; runId: number; applied_value: string }
    >({
      query: ({ id, applied_value }) => ({
        url: `/admin/od-review/issues/${id}/accept`,
        method: "PATCH",
        data: { applied_value },
      }),
      invalidatesTags: (result, error, { runId }) =>
        error ? [] : [{ type: "OdReviewIssue" as const, id: `RUN-${runId}` }],
    }),
    rejectOdReviewIssue: builder.mutation<
      OdReviewIssue,
      { id: number; runId: number }
    >({
      query: ({ id }) => ({
        url: `/admin/od-review/issues/${id}/reject`,
        method: "PATCH",
      }),
      invalidatesTags: (result, error, { runId }) =>
        error ? [] : [{ type: "OdReviewIssue" as const, id: `RUN-${runId}` }],
    }),
    markOdReviewIssueManual: builder.mutation<
      OdReviewIssue,
      { id: number; runId: number }
    >({
      query: ({ id }) => ({
        url: `/admin/od-review/issues/${id}/manual`,
        method: "PATCH",
      }),
      invalidatesTags: (result, error, { runId }) =>
        error ? [] : [{ type: "OdReviewIssue" as const, id: `RUN-${runId}` }],
    }),
    resolveOdReviewIssue: builder.mutation<
      OdReviewIssue,
      { id: number; runId: number }
    >({
      query: ({ id }) => ({
        url: `/admin/od-review/issues/${id}/resolve`,
        method: "PATCH",
      }),
      invalidatesTags: (result, error, { runId }) =>
        error ? [] : [{ type: "OdReviewIssue" as const, id: `RUN-${runId}` }],
    }),
  }),
});

export const {
  useGetOdReviewCoursesQuery,
  useGetOdReviewRunsQuery,
  useGetOdReviewRunQuery,
  useCreateOdReviewRunMutation,
  useGetOdReviewIssuesQuery,
  useAcceptOdReviewIssueMutation,
  useRejectOdReviewIssueMutation,
  useMarkOdReviewIssueManualMutation,
  useResolveOdReviewIssueMutation,
} = odReviewApi;
